/**
 * Seed canónico de los 7 riesgos iniciales del PEP FAC Colombia.
 *
 * Fuente:
 *   - `docs/Gestion_Riesgos_Proyectos_FAC_Colombia.xlsx` hoja RISK REGISTER
 *   - `docs/Capitulo_Gestion_Riesgos_PEP_v2.docx` sección 9
 *
 * Se carga vía `ensureSeedRiesgos(tenantId)` la primera vez que se abre
 * el módulo. Idempotente: si ya existen riesgos para el tenant, no hace
 * nada (no sobreescribe trabajo del usuario).
 */

import { buildRepos } from "@core/data/repos";
import { GERENCIA_DEFAULT } from "@constants/gerencias";
import type {
  AreaImpacto,
  EstadoRiesgo,
  EstrategiaRespuesta,
} from "@app-types/entities";

interface RiesgoSeedSpec {
  descripcion: string;
  causaRaiz: string;
  efecto: string;
  categoriaRbsCodigo: string;
  areaImpacto: AreaImpacto[];
  probabilidad: 1 | 2 | 3 | 4 | 5;
  impacto: 1 | 2 | 3 | 4 | 5;
  tipo: "AMENAZA" | "OPORTUNIDAD";
  estrategiaRespuesta: EstrategiaRespuesta;
  planMitigacion: string;
  responsableEmail: string;
  fechaLimite: string;
  estado: EstadoRiesgo;
  sprintAsignado: string;
  indicadorSeguimiento: string;
  triggerAlerta: string;
  planContingencia: string;
  fuenteEvidencia: string;
  fechaIdentificacion: string;
  /**
   * Fase 4 del ADR — flag de compartido cross-gerencia. Algunos riesgos
   * "clásicos" (mercado, regulatorios, transformación digital) son
   * marcados como compartidos en el seed para demostrar la mecánica
   * cross-gerencia desde el primer arranque.
   */
  compartido?: boolean;
}

/**
 * Los 7 riesgos del PEP FAC Colombia v1.0 (mayo 2026).
 * Reconciliados entre el Risk Register Excel y la sección 9 del PEP.
 */
const RIESGOS_FAC: RiesgoSeedSpec[] = [
  // R-001 — CRÍTICO — Sprint 2
  {
    descripcion:
      "Retrasos recurrentes en entrega de equipos por proveedores. Caso: Generación Power con Power Group (4.4 → 10.6 MMUSD, >12 meses de retraso).",
    causaRaiz:
      "1) Falta anexo técnico por disciplina. 2) Incumplimiento RETIE/NTC2050/NFPA70E. 3) Ausencia de electricista certificado. 4) Equipos recibidos en deterioro.",
    efecto:
      "Extensión de cronograma > 12 meses, sobrecosto 140%, cambio de proveedor CAT→STX con 22 meses adicionales.",
    categoriaRbsCodigo: "2.1",
    areaImpacto: ["Cronograma", "Costo"],
    probabilidad: 5,
    impacto: 5,
    tipo: "AMENAZA",
    estrategiaRespuesta: "MITIGAR",
    planMitigacion:
      "1) Anexo Técnico Estándar por disciplina. 2) Cláusulas de penalización por retraso. 3) Verificación de certificaciones RETIE. 4) FAT obligatoria antes del despacho.",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    fechaLimite: "2026-06-30",
    estado: "IDENTIFICADO",
    sprintAsignado: "2026-06",
    indicadorSeguimiento: "% avance vs cronograma; SPI del proyecto",
    triggerAlerta: "Desviación >15% en cronograma; equipos sin certificación FAT",
    planContingencia:
      "Activar proveedor alterno precalificado; escalar a DRB para definir mitigación complementaria.",
    fuenteEvidencia: "Lecciones Aprendidas Generación Power; Comité Obra Pendare",
    fechaIdentificacion: "2024-10-01",
  },
  // R-002 — ALTO — Sprint 1
  {
    descripcion:
      "Ineficiencias en proceso de adquisiciones ABAS/FAC: PRs incompletas, sin KOM, documentación equivocada, proveedores sin habilitación HSE.",
    causaRaiz:
      "1) Desconocimiento del proceso de compras. 2) Falta de estructura en PRs. 3) Comunicación deficiente entre turnos. 4) Proveedores sin requisitos.",
    efecto:
      "Reprocesos, demoras >90 días, sobrecostos de emergencia, retrasos en facturación.",
    categoriaRbsCodigo: "4.1",
    areaImpacto: ["Costo", "Cronograma"],
    probabilidad: 5,
    impacto: 4,
    tipo: "AMENAZA",
    estrategiaRespuesta: "MITIGAR",
    planMitigacion:
      "1) Definition of Ready para PRs. 2) KOM obligatoria para requerimientos complejos. 3) Capacitación LEAN FAC+ABAS. 4) Vendor List actualizado.",
    responsableEmail: "rafael.garcia@tecpetrol.com",
    fechaLimite: "2026-07-31",
    estado: "EN_ANALISIS",
    sprintAsignado: "2026-06",
    indicadorSeguimiento: "Tiempo promedio de gestión de PR; % PRs rechazadas",
    triggerAlerta: "PR devuelta incompleta; tiempo de gestión >120 días",
    planContingencia: "RVU justificada para contingencias específicas.",
    fuenteEvidencia: "LEAN BACK OFFICE ABAS/FAC; Plan de acción",
    fechaIdentificacion: "2025-04-14",
    // Compartido: el proceso de adquisiciones ABAS aplica a todas las gerencias.
    compartido: true,
  },
  // R-003 — ALTO — Sprint 3
  {
    descripcion:
      "Brechas en madurez del sistema de gestión de proyectos (PEP incompleto, falta de plantillas, gobernanza informal).",
    causaRaiz:
      "1) Plantillas corporativas pendientes (Anexo XVI). 2) Rotación de personal. 3) Falta de capacitación formal en gestión de proyectos.",
    efecto:
      "Decisiones sin información completa, inconsistencia entre proyectos, dificultad para auditar.",
    categoriaRbsCodigo: "3.1",
    areaImpacto: ["Calidad", "Cronograma"],
    probabilidad: 3,
    impacto: 4,
    tipo: "AMENAZA",
    estrategiaRespuesta: "MITIGAR",
    planMitigacion:
      "1) Completar PEP con todos los anexos. 2) Capacitación PMI PMBOK al equipo. 3) Plantillas estandarizadas por etapa.",
    responsableEmail: "eduardo.zambrano@tecpetrol.com",
    fechaLimite: "2026-08-31",
    estado: "EN_ANALISIS",
    sprintAsignado: "2026-07",
    indicadorSeguimiento: "# plantillas completadas / # totales del Anexo XVI",
    triggerAlerta: "Auditoría interna con hallazgos repetidos",
    planContingencia:
      "Solicitar apoyo a PMO corporativa de Tecpetrol Argentina.",
    fuenteEvidencia: "Guía CARIDE (Abril 2025); Anexo XVI por desarrollar",
    fechaIdentificacion: "2026-04-01",
    // Compartido: madurez del sistema de gestión es transversal a todas las gerencias.
    compartido: true,
  },
  // R-004 — ALTO — Sprint 2 — HSE seguridad procesos
  {
    descripcion:
      "Riesgos de seguridad de procesos: espuma contra incendios con activación manual sin válvula diluvio; sistema de Hidrocarburos Operacionales (HO) sin extinción de incendios.",
    causaRaiz:
      "1) Diseño sin válvula diluvio aguas arriba del tanque de espuma. 2) Falta de detección/alarma y sistema de extinción en HO.",
    efecto:
      "Riesgo de lesión/fatalidad; respuesta excesiva en emergencia; potencial error humano en activación manual.",
    categoriaRbsCodigo: "1.4",
    areaImpacto: ["Seguridad"],
    probabilidad: 3,
    impacto: 5,
    tipo: "AMENAZA",
    estrategiaRespuesta: "EVITAR",
    planMitigacion:
      '1) Ingeniería ADVE: válvula diluvio 4", CO2, detección. 2) Filosofía de aislamiento alineada con guías Ecopetrol+HSE Institute. 3) Activación remota desde sala de control.',
    responsableEmail: "nestor.contreras@tecpetrol.com",
    fechaLimite: "2026-08-31",
    estado: "IDENTIFICADO",
    sprintAsignado: "2026-07",
    indicadorSeguimiento:
      "# ingenierías completadas; % recomendaciones implementadas",
    triggerAlerta: "Incidente o cuasi-incidente; auditoría aseguradora",
    planContingencia: "Aislamiento total del sistema y parada de emergencia.",
    fuenteEvidencia: "Proyectos HSE Ingeniería; Cotización ADVE",
    fechaIdentificacion: "2025-12-15",
  },
  // R-005 — MEDIO-ALTO — Sprint 2 — Calidad contratistas
  {
    descripcion:
      "Deficiencias en calidad de ejecución de contratistas: inasistencia a comités de obra, incumplimiento de compromisos, falta de registros de soldadura.",
    causaRaiz:
      "1) Supervisión insuficiente de documentos. 2) Requisitos no exigidos con rigor. 3) Rotación de personal del contratista. 4) Sin evaluación de desempeño formal.",
    efecto:
      "Retrabajos, no conformidades (NC), retrasos en cierre de contratos, riesgos de integridad mecánica a futuro.",
    categoriaRbsCodigo: "2.1",
    areaImpacto: ["Calidad", "Cronograma"],
    probabilidad: 4,
    impacto: 3,
    tipo: "AMENAZA",
    estrategiaRespuesta: "MITIGAR",
    planMitigacion:
      "1) Asistencia obligatoria a comités. 2) Registros de soldadura/reparación antes del cierre. 3) Checklist de cierre administrativo. 4) Evaluación EVALUA con impacto real.",
    responsableEmail: "sebastian.laverde@tecpetrol.com",
    fechaLimite: "2026-06-30",
    estado: "EN_MITIGACION",
    sprintAsignado: "2026-06",
    indicadorSeguimiento: "% compromisos cumplidos en comité; # NC por contratista",
    triggerAlerta:
      "Contratista con >3 NC abiertas; intento de desmovilización sin paz y salvo",
    planContingencia:
      "Suspensión del frente de trabajo; escalamiento a gerencia.",
    fuenteEvidencia: "Comité Obra Pendare; Contrato 6009 JOULES",
    fechaIdentificacion: "2026-05-08",
  },
  // R-006 — ALTO — Sprint 3 — Sobrecostos
  {
    descripcion:
      "Sobrecostos presupuestarios y desviaciones de alcance sin control formal: cambios mayores autorizados verbalmente.",
    causaRaiz:
      "1) Falta de procedimiento de gestión de cambios. 2) Presión operativa para no detener obra. 3) DRB sin reuniones programadas.",
    efecto:
      "CPI por debajo de 0.9 sostenido, sobreejecución del AFE, deterioro del flujo de caja del proyecto.",
    categoriaRbsCodigo: "3.3",
    areaImpacto: ["Costo", "Alcance"],
    probabilidad: 4,
    impacto: 3,
    tipo: "AMENAZA",
    estrategiaRespuesta: "MITIGAR",
    planMitigacion:
      "1) Procedimiento formal de Change Request (CR). 2) DRB quincenal con agenda fija. 3) Dashboard de CPI/SPI en TACO FAC. 4) Reserva de contingencia 5-10% por AFE.",
    responsableEmail: "nahuel.allo@tecpetrol.com",
    fechaLimite: "2026-07-31",
    estado: "IDENTIFICADO",
    sprintAsignado: "2026-07",
    indicadorSeguimiento: "CPI mensual; # CRs aprobados/rechazados",
    triggerAlerta: "CPI < 0.9 por 2 meses consecutivos",
    planContingencia: "Revisión de techo de AFE con DRB y JEFE.",
    fuenteEvidencia: "Reportes mensuales Tecpetrol Colombia",
    fechaIdentificacion: "2026-03-01",
  },
  // R-007 (R-006 en el Excel) — OPORTUNIDAD — Sprint 3 — App TACO FAC
  {
    descripcion:
      "Oportunidad: Transformación digital con App TACO FAC (PMIS) — integrar riesgos, HSE proveedores y alertas en plataforma unificada.",
    causaRaiz:
      "1) Interés de NEO Sede en digitalización. 2) Experiencia previa con Copilot/Power Automate. 3) Necesidad de visión integral de proyectos.",
    efecto:
      "Aceleración de la gestión ágil; trazabilidad en tiempo real; reducción de tiempos de respuesta a alertas.",
    categoriaRbsCodigo: "3.1",
    areaImpacto: ["Calidad", "Cronograma"],
    probabilidad: 2,
    impacto: 3,
    tipo: "OPORTUNIDAD",
    estrategiaRespuesta: "EXPLOTAR",
    planMitigacion:
      "1) Fase de pruebas con NEO. 2) Módulo de evaluación de riesgos. 3) Alertas automáticas vía Power Automate. 4) Piloto BDG 26-27.",
    responsableEmail: "rafael.garcia@tecpetrol.com",
    fechaLimite: "2026-12-31",
    estado: "EN_MITIGACION",
    sprintAsignado: "2026-08",
    indicadorSeguimiento: "# módulos implementados; # usuarios activos",
    triggerAlerta:
      "Aprobación de NEO para producción; revisión de código por IT",
    planContingencia: "MVP en Excel + Power Automate si el desarrollo se atrasa.",
    fuenteEvidencia: "App TACO FAC; Propuesta ALLO Nahuel",
    fechaIdentificacion: "2026-03-01",
    // Compartido: la transformación digital del PMIS es claramente cross-gerencia.
    compartido: true,
  },
];

/**
 * Asegura que los riesgos canónicos del PEP estén cargados.
 * Idempotente: solo siembra si la tabla está vacía para el tenant.
 *
 * @returns número de riesgos creados (0 si ya había datos).
 */
export async function ensureSeedRiesgos(
  tenantId: string,
  userEmail: string = "system@seed",
): Promise<number> {
  // En modo operación los seeds están bloqueados — el admin pidió que la
  // base permanezca como está y se respete su carga de datos reales.
  const { estaEnModoOperacion } = await import("@core/data/modoOperacion");
  if (estaEnModoOperacion()) return 0;
  const repo = buildRepos(tenantId).riesgo;
  const existentes = await repo.list();
  if (existentes.length > 0) return 0;

  let creados = 0;
  for (const spec of RIESGOS_FAC) {
    await repo.create(
      {
        ...spec,
        pinneadoAfeIds: [],
        vinculadoEventoIds: [],
        vinculadoLeccionIds: [],
        // Fase 3+4 del ADR — los riesgos seed pertenecen a la gerencia
        // natural (CO) y algunos llevan flag `compartido` para demo
        // cross-gerencia (mercado, regulatorios, transformación digital).
        gerenciaId: GERENCIA_DEFAULT,
        compartido: spec.compartido === true,
      },
      userEmail,
    );
    creados++;
  }
  return creados;
}
